import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { Surcharge } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

interface PaginatedSurchargeResponse {
  data: Surcharge[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetSurcharge = () => {
  const [surcharges, setSurcharges] = useState<Surcharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchSurcharges = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'name',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedSurchargeResponse>(
        apiUrls.SURCHARGES_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setSurcharges(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setSurcharges([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteSurcharge = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_SURCHARGE,
        {
          params: { id },
        }
      );

      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return {
    loading,
    surcharges,
    totalItems,
    totalPages,
    currentPage,
    fetchSurcharges,
    deleteSurcharge,
  };
};

export const getBasicSurcharges = async () => {
  const response = await axiosTokenInstance.get('surcharges/list-basic');
  return response.data.data;
};
